/**
 * What the call-off dialogs say about searching for a replacement, from the backend gate's decision.
 *
 * One pure mapping shared by both removal surfaces (the Day view / week-menu removal dialog and the
 * Staffing Changes "Find Replacement" popup) so their default, their primary button and their wording
 * cannot drift. The wording follows the gate's `basis`, never a guess: a ratio department is never told
 * about HPPD, and a department where no calculation ran is never told one did.
 */
import type { InlineHintTone } from "@/ui/molecules/InlineHint";
import { CallOffGateBasis, CallOffReplacementOutcome } from "./models";

export type ReplacementRecommendation = "search" | "skip";

export type CallOffReplacementHint = { messageKey: string; tone: InlineHintTone };

export type CallOffReplacementAdvice = {
	/** Whether the "search for a replacement" checkbox starts ticked. Mirrors the backend's `should_find_replacement`. */
	defaultTick: boolean;
	/** Null when the gate could not form a view — the manager decides and neither choice is favoured. */
	recommendation: ReplacementRecommendation | null;
	/** The note that explains the recommendation, or null when there is nothing computed to say. */
	message: CallOffReplacementHint | null;
	/**
	 * When the checkbox surface shows `message`: only while the tick goes AGAINST the recommendation (the
	 * computed cases — agreeing with it needs no comment), or regardless of the tick (the "couldn't check"
	 * cases, where the note is the explanation of the default itself).
	 */
	showMessage: "against_recommendation" | "always";
};

const warn = (messageKey: string): CallOffReplacementHint => ({ messageKey, tone: "warning" });

const COMPUTED_MESSAGES: Record<CallOffGateBasis.HPPD | CallOffGateBasis.RATIO, { needed: string; notNeeded: string }> = {
	[CallOffGateBasis.HPPD]: { needed: "hppd.replacement.needed", notNeeded: "hppd.ok.no.replacement.needed" },
	[CallOffGateBasis.RATIO]: { needed: "ratio.replacement.needed", notNeeded: "ratio.ok.no.replacement.needed" },
};

/** The outcomes the replacement-gate preview can send. */
type PreviewOutcome = Exclude<CallOffReplacementOutcome, CallOffReplacementOutcome.NOT_STARTED>;

/** The outcomes each basis comes with — mirrors where the backend builds its `CallOffGateDecision`. */
const OUTCOMES_BY_BASIS: Record<CallOffGateBasis, readonly PreviewOutcome[]> = {
	[CallOffGateBasis.ALWAYS_SEARCH]: [CallOffReplacementOutcome.SEARCHING],
	[CallOffGateBasis.UNAVAILABLE]: [CallOffReplacementOutcome.SEARCHING],
	[CallOffGateBasis.HPPD]: [CallOffReplacementOutcome.SEARCHING, CallOffReplacementOutcome.SUFFICIENT_STAFF],
	[CallOffGateBasis.RATIO]: [CallOffReplacementOutcome.SEARCHING, CallOffReplacementOutcome.SUFFICIENT_STAFF, CallOffReplacementOutcome.NO_LOCATION_MATCH],
};

/** The advice for a gate that actually computed coverage (HPPD floor or ratio ladder). */
const computedAdvice = (outcome: PreviewOutcome, basis: CallOffGateBasis.HPPD | CallOffGateBasis.RATIO): CallOffReplacementAdvice => {
	const messages = COMPUTED_MESSAGES[basis];
	switch (outcome) {
		case CallOffReplacementOutcome.SEARCHING:
			return { defaultTick: true, recommendation: "search", message: warn(messages.needed), showMessage: "against_recommendation" };
		case CallOffReplacementOutcome.SUFFICIENT_STAFF:
			return { defaultTick: false, recommendation: "skip", message: warn(messages.notNeeded), showMessage: "against_recommendation" };
		case CallOffReplacementOutcome.NO_LOCATION_MATCH:
			// The ratio gate found no staffed location for this shift, so coverage was never checked. It must
			// not read as "you may not need to search" — that would be a claim about a calculation that didn't run.
			return { defaultTick: false, recommendation: null, message: warn("call.off.gate.no.location.match"), showMessage: "always" };
	}
};

export const callOffReplacementAdvice = (outcome: CallOffReplacementOutcome, basis: CallOffGateBasis): CallOffReplacementAdvice => {
	// Only an explicit decline produces this, and the preview never passes one — on any basis. Thrown rather
	// than quietly defaulting to "don't search": `useCallOffReplacementGate` maps it through `select`, so the
	// throw becomes the query's error and the dialog reports it instead of hiding a backend change.
	if (outcome === CallOffReplacementOutcome.NOT_STARTED) {
		throw new Error(`No call-off replacement advice for outcome "${outcome}" on basis "${basis}"`);
	}
	// A backend that predates `basis` (a deploy ships the frontend first) sends none; the switch below would
	// then return nothing and crash the dialog, so it fails into the gate error path the same way.
	if (!Object.values(CallOffGateBasis).includes(basis)) {
		throw new Error(`No call-off replacement advice for unknown basis "${basis}"`);
	}
	// An outcome the backend added first, or one its basis never comes with, would otherwise pick advice for a
	// decision the gate didn't make (e.g. an unticked box that recommends searching), so it fails the same way.
	if (!OUTCOMES_BY_BASIS[basis].includes(outcome)) {
		throw new Error(`No call-off replacement advice for outcome "${outcome}" on basis "${basis}"`);
	}
	switch (basis) {
		case CallOffGateBasis.ALWAYS_SEARCH:
			// This department searches on every call-off. Nothing was calculated, so the note claims nothing
			// about coverage — it only says why the box starts ticked, once someone unticks it.
			return {
				defaultTick: true,
				recommendation: "search",
				message: { messageKey: "call.off.gate.always.search", tone: "info" },
				showMessage: "against_recommendation",
			};
		case CallOffGateBasis.UNAVAILABLE:
			return {
				defaultTick: true,
				recommendation: "search",
				message: { messageKey: "call.off.gate.unavailable", tone: "info" },
				showMessage: "always",
			};
		case CallOffGateBasis.HPPD:
		case CallOffGateBasis.RATIO:
			return computedAdvice(outcome, basis);
	}
};

/**
 * Whether the checkbox surface shows `advice.message` for the current tick.
 *
 * A boolean rather than the hint itself so the caller can keep rendering the (per-gate, stable) message
 * and let this drive `InlineHint`'s `open` — handing back null on a tick would empty the note mid-exit.
 */
export const isCheckboxHintOpen = (advice: CallOffReplacementAdvice, ticked: boolean): boolean => {
	if (advice.message === null) return false;
	if (advice.showMessage === "always") return true;
	switch (advice.recommendation) {
		case "search":
			return !ticked;
		case "skip":
			return ticked;
		case null:
			return false;
	}
};

type ChoiceVariant = "primary" | "secondary";

/**
 * Which of the popup's two choices is promoted. Follows the recommendation, and promotes NEITHER when there
 * is none (coverage couldn't be checked, or the gate is still unknown) — the manager decides unprompted.
 */
export const replacementChoiceVariants = (
	recommendation: ReplacementRecommendation | null | undefined,
): { search: ChoiceVariant; removeOnly: ChoiceVariant } => ({
	search: recommendation === "search" ? "primary" : "secondary",
	removeOnly: recommendation === "skip" ? "primary" : "secondary",
});
